import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, MapPin, Baby, Heart, Users } from 'lucide-react';
import axios from 'axios';
import { API } from '@/utils/api';

const TYPE_LABELS = {
  szczenieta: 'Szczenięta',
  hodowle: 'Hodowle',
  reproduktory: 'Reproduktory',
};

const VOIVODESHIPS = [
  'Dolnośląskie', 'Kujawsko-Pomorskie', 'Lubelskie', 'Lubuskie',
  'Łódzkie', 'Małopolskie', 'Mazowieckie', 'Opolskie', 'Podkarpackie',
  'Podlaskie', 'Pomorskie', 'Śląskie', 'Świętokrzyskie', 'Warmińsko-Mazurskie',
  'Wielkopolskie', 'Zachodniopomorskie',
];

// Główne miasta per województwo (max 10, posortowane wg wielkości)
const CITIES_BY_VOIVODESHIP = {
  'Dolnośląskie': ['Wrocław', 'Wałbrzych', 'Legnica', 'Jelenia Góra', 'Lubin', 'Głogów', 'Świdnica', 'Bolesławiec', 'Oleśnica', 'Zgorzelec'],
  'Kujawsko-Pomorskie': ['Bydgoszcz', 'Toruń', 'Włocławek', 'Grudziądz', 'Inowrocław', 'Brodnica', 'Świecie', 'Chełmno', 'Nakło nad Notecią'],
  'Lubelskie': ['Lublin', 'Chełm', 'Zamość', 'Biała Podlaska', 'Puławy', 'Świdnik', 'Łuków', 'Kraśnik', 'Biłgoraj'],
  'Lubuskie': ['Zielona Góra', 'Gorzów Wielkopolski', 'Nowa Sól', 'Żary', 'Żagań', 'Świebodzin', 'Sulechów'],
  'Łódzkie': ['Łódź', 'Piotrków Trybunalski', 'Pabianice', 'Tomaszów Mazowiecki', 'Bełchatów', 'Zgierz', 'Skierniewice', 'Sieradz', 'Radomsko', 'Kutno'],
  'Małopolskie': ['Kraków', 'Tarnów', 'Nowy Sącz', 'Oświęcim', 'Chrzanów', 'Olkusz', 'Nowy Targ', 'Bochnia', 'Zakopane', 'Wieliczka'],
  'Mazowieckie': ['Warszawa', 'Radom', 'Płock', 'Siedlce', 'Pruszków', 'Legionowo', 'Ostrołęka', 'Ciechanów', 'Otwock', 'Piaseczno'],
  'Opolskie': ['Opole', 'Kędzierzyn-Koźle', 'Nysa', 'Brzeg', 'Kluczbork', 'Prudnik', 'Strzelce Opolskie'],
  'Podkarpackie': ['Rzeszów', 'Przemyśl', 'Stalowa Wola', 'Mielec', 'Tarnobrzeg', 'Krosno', 'Dębica', 'Jarosław', 'Sanok', 'Jasło'],
  'Podlaskie': ['Białystok', 'Suwałki', 'Łomża', 'Augustów', 'Bielsk Podlaski', 'Hajnówka', 'Grajewo'],
  'Pomorskie': ['Gdańsk', 'Gdynia', 'Sopot', 'Słupsk', 'Tczew', 'Starogard Gdański', 'Rumia', 'Wejherowo', 'Chojnice', 'Kwidzyn'],
  'Śląskie': ['Katowice', 'Częstochowa', 'Sosnowiec', 'Gliwice', 'Zabrze', 'Bielsko-Biała', 'Bytom', 'Ruda Śląska', 'Rybnik', 'Tychy'],
  'Świętokrzyskie': ['Kielce', 'Ostrowiec Świętokrzyski', 'Starachowice', 'Skarżysko-Kamienna', 'Sandomierz', 'Końskie', 'Jędrzejów'],
  'Warmińsko-Mazurskie': ['Olsztyn', 'Elbląg', 'Ełk', 'Ostróda', 'Iława', 'Giżycko', 'Kętrzyn', 'Szczytno', 'Bartoszyce'],
  'Wielkopolskie': ['Poznań', 'Kalisz', 'Konin', 'Piła', 'Ostrów Wielkopolski', 'Gniezno', 'Leszno', 'Swarzędz', 'Luboń', 'Śrem'],
  'Zachodniopomorskie': ['Szczecin', 'Koszalin', 'Stargard', 'Kołobrzeg', 'Świnoujście', 'Szczecinek', 'Police', 'Wałcz', 'Goleniów'],
};

// Wszystkie miasta — dla autocomplete gdy województwo nie jest wybrane
const ALL_CITIES = Array.from(new Set(Object.values(CITIES_BY_VOIVODESHIP).flat())).sort();

/**
 * Globalny SearchBar — używany na Homepage i stronach ras.
 *
 * UWAGA (v2 P0): TYPE NIE jest wybierany w SearchBarze.
 * Źródłem prawdy dla typu jest URL `?type=…` ustawiany przez TopNavBar.
 */
export default function SearchBar({ initial = {}, lockBreed = false, onSearch }) {
  const navigate = useNavigate();
  const [type, setType] = useState(() => {
    // Normalizuj alias'y URL do canonical value w selektorze
    const t = initial.type;
    if (t === 'mioty' || t === 'szczenieta' || t === 'puppies') return 'szczenieta';
    if (t === 'reproduktory' || t === 'reproduktora' || t === 'studs') return 'reproduktory';
    if (t === 'hodowle' || t === 'kennels') return 'hodowle';
    return 'szczenieta';
  });
  const [breed, setBreed] = useState(initial.breed || '');
  const [city, setCity] = useState(initial.city || '');
  const [voivodeship, setVoivodeship] = useState(initial.voivodeship || '');
  const [breedsList, setBreedsList] = useState([]);
  const [dynamicCities, setDynamicCities] = useState([]); // [{city, count}]
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [breedOpen, setBreedOpen] = useState(false);
  const [voivOpen, setVoivOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const breedRef = useRef(null);
  const voivRef = useRef(null);
  const cityRef = useRef(null);
  const typeRef = useRef(null);

  // Re-sync gdy user zmieni typ przez TopNavBar (URL change) — bez tego SearchBar pokazywał stary typ
  useEffect(() => {
    const t = initial.type;
    if (t === 'mioty' || t === 'szczenieta' || t === 'puppies') setType('szczenieta');
    else if (t === 'reproduktory' || t === 'reproduktora' || t === 'studs') setType('reproduktory');
    else if (t === 'hodowle' || t === 'kennels') setType('hodowle');
  }, [initial.type]);

  useEffect(() => {
    axios.get(`${API}/public/rasy`).then(res => setBreedsList(res.data.breeds || [])).catch(() => {});
  }, []);

  // Dynamiczny endpoint miast — filtruje po województwie jeśli wybrane
  useEffect(() => {
    const ctrl = new AbortController();
    setCitiesLoading(true);
    const params = { limit: 40 };
    if (voivodeship) params.wojewodztwo = voivodeship;
    axios.get(`${API}/public/cities`, { params, signal: ctrl.signal })
      .then(res => setDynamicCities(res.data.items || []))
      .catch(() => setDynamicCities([])) // fallback na hardcoded obsłużony w cityPool
      .finally(() => setCitiesLoading(false));
    return () => ctrl.abort();
  }, [voivodeship]);

  useEffect(() => {
    const close = (e) => {
      if (breedRef.current && !breedRef.current.contains(e.target)) setBreedOpen(false);
      if (voivRef.current && !voivRef.current.contains(e.target)) setVoivOpen(false);
      if (cityRef.current && !cityRef.current.contains(e.target)) setCityOpen(false);
      if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const filteredBreeds = useMemo(() => {
    const q = breed.trim().toLowerCase();
    if (!q) {
      // bez zapytania: najpierw rasy z treścią, potem pozostałe, alfabetycznie
      return [...breedsList]
        .sort((a, b) => Number(b.has_content) - Number(a.has_content) || a.name.localeCompare(b.name, 'pl'))
        .slice(0, 10);
    }
    const matches = breedsList.filter(b => b.name.toLowerCase().includes(q));
    // ranking: exact > startsWith > has_content > reszta
    matches.sort((a, b) => {
      const la = a.name.toLowerCase();
      const lb = b.name.toLowerCase();
      const exactA = la === q ? 0 : 1;
      const exactB = lb === q ? 0 : 1;
      if (exactA !== exactB) return exactA - exactB;
      const startA = la.startsWith(q) ? 0 : 1;
      const startB = lb.startsWith(q) ? 0 : 1;
      if (startA !== startB) return startA - startB;
      if (a.has_content !== b.has_content) return Number(b.has_content) - Number(a.has_content);
      return a.name.localeCompare(b.name, 'pl');
    });
    return matches.slice(0, 10);
  }, [breed, breedsList]);

  // Źródło miast — dynamiczne (z realnej bazy hodowli), fallback do hardcoded
  const cityPool = useMemo(() => {
    if (dynamicCities.length > 0) {
      return dynamicCities; // [{city, count, voivodeship}]
    }
    const fallbackList = voivodeship && CITIES_BY_VOIVODESHIP[voivodeship]
      ? CITIES_BY_VOIVODESHIP[voivodeship]
      : ALL_CITIES;
    return fallbackList.map(c => ({ city: c, count: 0 }));
  }, [dynamicCities, voivodeship]);

  const filteredCities = useMemo(() => {
    const qs = city.trim().toLowerCase();
    const pool = qs
      ? cityPool.filter(c => c.city.toLowerCase().includes(qs))
      : cityPool;
    return pool.slice(0, 12);
  }, [city, cityPool]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const filters = { type, breed, city, voivodeship };
    if (onSearch) return onSearch(filters);
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (breed) params.set('rasa', breed);
    if (city) params.set('miasto', city);
    if (voivodeship) params.set('wojewodztwo', voivodeship);
    navigate(`/katalog?${params.toString()}`);
  };

  // Gdy user zmienia województwo → wyczyść miasto (nowa lista miast się załaduje z backendu)
  const handleVoivodeshipChange = (v) => {
    setVoivodeship(v);
    setVoivOpen(false);
    if (v && city) setCity('');
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="relative z-30 w-full bg-[#10182B]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-3 flex flex-col md:flex-row items-stretch gap-2 shadow-xl"
      data-testid="global-searchbar"
    >
      {/* TYPE — co szukamy */}
      <div ref={typeRef} className="relative w-full md:w-44 shrink-0">
        <button
          type="button"
          onClick={() => setTypeOpen(v => !v)}
          data-testid="searchbar-type-trigger"
          className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white flex items-center justify-between gap-2 hover:bg-white/10 transition"
        >
          <span className="inline-flex items-center gap-1.5 truncate">
            {type === 'hodowle' && <Users className="w-4 h-4 text-blue-400" />}
            {type === 'szczenieta' && <Baby className="w-4 h-4 text-emerald-400" />}
            {type === 'reproduktory' && <Heart className="w-4 h-4 text-purple-400" />}
            <span>{TYPE_LABELS[type]}</span>
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition shrink-0 ${typeOpen ? 'rotate-180' : ''}`} />
        </button>
        {typeOpen && (
          <div className="absolute top-14 left-0 right-0 z-50 bg-[#10182B] border border-white/10 rounded-xl shadow-2xl overflow-hidden" data-testid="searchbar-type-dropdown">
            {[
              { key: 'szczenieta', label: 'Szczenięta', Icon: Baby, color: 'text-emerald-400' },
              { key: 'hodowle', label: 'Hodowle', Icon: Users, color: 'text-blue-400' },
              { key: 'reproduktory', label: 'Reproduktory', Icon: Heart, color: 'text-purple-400' },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => { setType(opt.key); setTypeOpen(false); }}
                data-testid={`searchbar-type-option-${opt.key}`}
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-white/5 inline-flex items-center gap-2 ${
                  type === opt.key ? 'bg-white/5 text-white font-semibold' : 'text-white/80'
                }`}
              >
                <opt.Icon className={`w-4 h-4 ${opt.color}`} />
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* BREED AUTOCOMPLETE */}
      <div ref={breedRef} className="relative flex-1 min-w-0">
        <input
          type="text"
          value={breed}
          onChange={(e) => { setBreed(e.target.value); setBreedOpen(true); }}
          onFocus={() => !lockBreed && setBreedOpen(true)}
          placeholder="Rasa (np. Labrador, Yorkshire)"
          disabled={lockBreed}
          className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-500 focus:border-blue-500/50 outline-none disabled:opacity-70"
          data-testid="searchbar-breed-input"
        />
        {lockBreed && breed && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-300 rounded-full">prefill</div>
        )}
        {!lockBreed && breedOpen && filteredBreeds.length > 0 && (
          <div className="absolute top-14 left-0 right-0 z-50 bg-[#10182B] border border-white/10 rounded-xl shadow-2xl max-h-72 overflow-y-auto" data-testid="searchbar-breed-dropdown">
            {filteredBreeds.map(b => (
              <button
                type="button"
                key={b.slug}
                onClick={() => { setBreed(b.name); setBreedOpen(false); }}
                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/5 flex items-center justify-between"
                data-testid={`searchbar-breed-option-${b.slug}`}
              >
                <span>{b.name}</span>
                {b.has_content && <span className="text-xs text-emerald-400">●</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* CITY — autocomplete z listy miast per województwo */}
      <div ref={cityRef} className="relative w-full md:w-56 shrink-0">
        <input
          type="text"
          value={city}
          onChange={(e) => { setCity(e.target.value); setCityOpen(true); }}
          onFocus={() => setCityOpen(true)}
          placeholder={voivodeship ? `Miasto w ${voivodeship}` : 'Miasto (opcjonalnie)'}
          className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-500 focus:border-blue-500/50 outline-none"
          data-testid="searchbar-city-input"
          autoComplete="off"
        />
        {cityOpen && filteredCities.length > 0 && (
          <div className="absolute top-14 left-0 right-0 z-50 bg-[#10182B] border border-white/10 rounded-xl shadow-2xl max-h-72 overflow-y-auto" data-testid="searchbar-city-dropdown">
            <div className="px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-white/40 border-b border-white/5">
              {voivodeship ? `Popularne w ${voivodeship}` : 'Popularne miasta'}
              {citiesLoading && ' · ładowanie…'}
            </div>
            {filteredCities.map(c => (
              <button
                type="button"
                key={`${c.city}-${c.voivodeship || ''}`}
                onClick={() => { setCity(c.city); setCityOpen(false); }}
                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/5 flex items-center justify-between gap-2"
                data-testid={`searchbar-city-option-${c.city}`}
              >
                <span className="inline-flex items-center gap-2 truncate">
                  <MapPin className="w-3.5 h-3.5 text-white/40 shrink-0" />
                  <span className="truncate">{c.city}</span>
                </span>
                {c.count > 0 && (
                  <span className="text-[10px] text-white/40 tabular-nums shrink-0">
                    {c.count} {c.count === 1 ? 'hodowla' : c.count < 5 ? 'hodowle' : 'hodowli'}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* VOIVODESHIP (custom dropdown — natywny <select> ma problemy z kolorem na Mac/Chrome) */}
      <div ref={voivRef} className="relative w-full md:w-52 shrink-0">
        <button
          type="button"
          onClick={() => setVoivOpen(v => !v)}
          data-testid="searchbar-voivodeship-trigger"
          className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white flex items-center justify-between gap-2 hover:bg-white/10 transition"
        >
          <span className={voivodeship ? 'text-white truncate' : 'text-gray-500'}>
            {voivodeship || 'Cała Polska'}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition shrink-0 ${voivOpen ? 'rotate-180' : ''}`} />
        </button>
        {voivOpen && (
          <div className="absolute top-14 left-0 right-0 z-50 bg-[#10182B] border border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto" data-testid="searchbar-voivodeship-dropdown">
            <button
              type="button"
              onClick={() => handleVoivodeshipChange('')}
              className={`w-full px-4 py-2.5 text-left text-sm hover:bg-white/5 ${!voivodeship ? 'bg-white/5 text-white' : 'text-gray-400'}`}
              data-testid="searchbar-voivodeship-all"
            >
              Cała Polska
            </button>
            {VOIVODESHIPS.map(v => (
              <button
                type="button"
                key={v}
                onClick={() => handleVoivodeshipChange(v)}
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-white/5 ${voivodeship === v ? 'bg-blue-500/15 text-blue-300' : 'text-white'}`}
                data-testid={`searchbar-voivodeship-option-${v}`}
              >
                {v}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* SEARCH BUTTON */}
      <button
        type="submit"
        className="h-12 px-6 rounded-xl bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 transition-all hover:shadow-blue-500/40 active:scale-95"
        data-testid="searchbar-submit"
      >
        <Search className="w-4 h-4" />
        Szukaj
      </button>
    </form>
  );
}
